import React, { useEffect, useState } from "react";
import { IonButton, IonChip, IonCol, IonIcon, IonRow } from "@ionic/react";
import { addCircleOutline } from "ionicons/icons";
import { Membership } from "../../../models";
import {
    getMembershipsByHorseId,
    getMembershipsByOwnerId,
    getMembershipsByRiderId,
    getMembershipsByTrainerId,
} from "../../../utilities/membership/Membership";
import AddMembershipModal, { MembershipEntityType } from "./AddMembershipModal";

interface MembershipUIProps {
    entityType: MembershipEntityType;
    entityId?: string | null;
    /** When the entity has a name but may not be saved yet, save it and return the id. */
    ensureEntityExists?: () => Promise<string | null | undefined>;
    disabled?: boolean;
}

const loadMembershipsForEntity = async (entityType: MembershipEntityType, entityId: string) => {
    switch (entityType) {
        case "horse":
            return getMembershipsByHorseId(entityId);
        case "rider":
            return getMembershipsByRiderId(entityId);
        case "owner":
            return getMembershipsByOwnerId(entityId);
        case "trainer":
            return getMembershipsByTrainerId(entityId);
    }
};

const MembershipUI: React.FC<MembershipUIProps> = ({ entityType, entityId, ensureEntityExists, disabled }) => {
    const [memberships, setMemberships] = useState<Membership[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [modalEntityId, setModalEntityId] = useState<string | null | undefined>();

    const loadMemberships = async (id?: string | null) => {
        if (!id) {
            setMemberships([]);
            return;
        }
        const queryResult = await loadMembershipsForEntity(entityType, id);
        if (queryResult.isSuccess) {
            const next: Membership[] = (queryResult.result || []).sort((a: Membership, b: Membership) => a.name.localeCompare(b.name));
            setMemberships(next);
        } else {
            setMemberships([]);
        }
    };

    useEffect(() => {
        loadMemberships(entityId);
    }, [entityType, entityId]);

    const handleOpenMembershipModal = async () => {
        let idForModal = entityId;

        if (!idForModal && ensureEntityExists) {
            idForModal = await ensureEntityExists();
        }

        if (idForModal) {
            setModalEntityId(idForModal);
            setShowModal(true);
        }
    };

    return (
        <>
            <IonCol size="12" className="pt-2">
                <IonRow className="ion-align-items-center">
                    <IonCol sizeXs="12" sizeMd="8" className="p-0 pb-2 pb-md-0 pr-md-2">
                    {memberships.length > 0 && (
                            <div className="d-flex flex-wrap align-items-center" style={{ gap: "0.5rem" }}>
                                {memberships.map((membership) => {
                                    const displayName = membership.name.replace(/\s+Horse$/i, "").trim();
                                    return (
                                        <IonChip
                                            key={membership.id}
                                            className="m-0 rounded-2 font-weight-bold text-white bg-medium"
                                            style={{ paddingLeft: "10px", paddingRight: "10px" }}
                                            onClick={handleOpenMembershipModal}>
                                            {displayName}: {membership.membershipId}
                                        </IonChip>
                                    );
                                })}
                            </div>
                    )}
                    </IonCol>
                    <IonCol sizeXs="12" sizeMd="4" className="p-0">
                        <IonButton color="tertiary" expand="block" className="m-0 ion-button-wrap" disabled={disabled} onClick={handleOpenMembershipModal}>
                            <IonIcon icon={addCircleOutline} slot="start" />
                            Add or Edit Memberships
                        </IonButton>
                    </IonCol>
                </IonRow>
            </IonCol>

            <AddMembershipModal
                isOpen={showModal}
                entityType={entityType}
                entityId={modalEntityId}
                onClose={() => {
                    setShowModal(false);
                    setModalEntityId(undefined);
                }}
                onSaved={() => {
                    loadMemberships(modalEntityId || entityId);
                }}
            />
        </>
    );
};

export default MembershipUI;
